<script setup lang="ts">
import { computed } from 'vue';
import useLocale from '../../composables/useLocale';
import { useDisplay } from 'vuetify';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faBookOpen, faFile, faBookOpenLines, faLineColumns, faAlignJustify } from '@fortawesome/pro-regular-svg-icons';
import { storeToRefs } from 'pinia';
import useRenditionStore from '../../stores/rendition';
library.add(faBookOpen, faFile, faBookOpenLines, faLineColumns, faAlignJustify);

const pageDisplayMode = defineModel<'single' | 'spread'>({ required: true });

const { t } = useLocale();
const { smAndDown } = useDisplay();
const { isReflowable } = storeToRefs(useRenditionStore());

const options = computed(() => [
  {
    title: t('book_player.label_single_page_layout'),
    value: 'single',
    icon: isReflowable.value ? 'far fa-align-justify' : 'far fa-file',
  },
  {
    title: t('book_player.label_spread_page_layout'),
    value: 'spread',
    icon: isReflowable.value ? 'far fa-line-columns' : 'far fa-book-open',
  },
]);
</script>

<template>
  <v-list-item density="compact" data-testid="bp-setting-page-display" :title="t('book_player.label_page_layout')">
    <template #prepend>
      <FontAwesomeIcon :size="!smAndDown ? 'xl' : 'lg'" icon="far fa-book-open-lines" class="pr-3" />
    </template>
    <template #append>
      <v-btn-toggle
        v-model="pageDisplayMode"
        mandatory
        density="compact"
        variant="outlined"
        color="primary"
        class="page-display-btn-toggle my-1 ml-4 border-surface border-opacity-50"
      >
        <v-btn
          v-for="option in options"
          :key="option.value"
          :value="option.value"
          :aria-label="option.title"
          :readonly="option.value === pageDisplayMode"
          size="small"
          class="flex-fill-btn"
        >
          <FontAwesomeIcon :icon="option.icon" />
        </v-btn>
      </v-btn-toggle>
    </template>
  </v-list-item>
</template>

<style scoped lang="scss">
:deep(.v-icon) {
  opacity: 1 !important;
}

.page-display-btn-toggle {
  height: 40px;
  min-width: 180px;
}

.flex-fill-btn {
  flex: 1 1 0;
  min-width: 0;
}

:deep(.v-btn-toggle) {
  .v-btn {
    flex: 1 1 0;
    min-width: 0;
  }

  .v-btn--active {
    background-color: rgb(var(--v-theme-primary)) !important;
    color: rgb(var(--v-theme-on-primary)) !important;

    > .v-btn__overlay {
      opacity: 0 !important;
    }
  }
}
</style>
